import * as tus from "tus-js-client";
import { createSHA1 } from "hash-wasm";
import {
  isThetaError,
  type Upload,
  type UploadScope,
  type VideoUploadTicket,
} from "@theta/api-client";
import { thetaApi, dataOf } from "./theta";

/**
 * VIDEO UPLOAD, WHEN THE HOST IS BUNNY — OSO-645 V4.
 *
 * The bytes do NOT go through our backend. This asks `POST /uploads/video` for
 * a place to put the video, gets back the `Upload` row (already `processing`)
 * plus TUS credentials, and streams the file straight to Bunny with
 * `tus-js-client` — resumable, so a dropped phone connection continues rather
 * than restarts. The composer then holds the same kind of `Upload` it gets from
 * `POST /uploads`, and everything downstream (the tray, the post write, the
 * publish-when-ready wait) is identical.
 *
 * WHY A CLIENT-SIDE sha1. It is the idempotency fingerprint the server keys the
 * ticket on (so the same video into the same place mints ONE Bunny video, not
 * one per tap), and the Phase 0 spike measured it at ~13 s for 2 GB on a real
 * phone — imperceptible next to the upload. `hash-wasm` streams the file in
 * chunks, so a 2 GB video is never held in memory at once.
 */

const SHA1_CHUNK = 8 * 1024 * 1024;

/** Stream a File through SHA-1 without ever holding it all in memory. */
export async function sha1OfFile(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const hasher = await createSHA1();
  hasher.init();
  for (let offset = 0; offset < file.size; offset += SHA1_CHUNK) {
    const slice = file.slice(offset, offset + SHA1_CHUNK);
    hasher.update(new Uint8Array(await slice.arrayBuffer()));
    onProgress?.(Math.min(1, (offset + SHA1_CHUNK) / file.size));
  }
  return hasher.digest("hex");
}

export interface VideoUploadHandle {
  /** Resolves with the `processing` Upload row once the bytes are all up. */
  done: Promise<Upload>;
  /** Cancel the in-flight upload (the tray's remove-while-uploading). */
  abort: () => void;
}

/**
 * Mint a ticket and stream the file to Bunny. `onProgress` reports 0..1 across
 * BOTH phases — hashing, then uploading — so the tray shows one bar that does
 * not stall or jump backwards.
 *
 * Resume is `tus-js-client`'s own: keyed on the fingerprint below, a retried
 * upload of the same file finds its previous offset and continues. The store is
 * the browser's, so it survives a reload but is per-device (which is all a
 * resume can be).
 */
export function uploadVideo(
  file: File,
  {
    scope,
    onProgress,
    fallbackMessage = "Videoen kunne ikke uploades. Prøv igen.",
  }: {
    scope: UploadScope;
    onProgress?: (fraction: number) => void;
    fallbackMessage?: string;
  },
): VideoUploadHandle {
  let upload: tus.Upload | null = null;
  let aborted = false;

  const done = (async (): Promise<Upload> => {
    // Phase 1: the fingerprint. Weighted as the first ~15% of the bar — the
    // hash is fast next to the transfer, but on a big file it is not nothing.
    const sha1 = await sha1OfFile(file, (f) => onProgress?.(f * 0.15));
    if (aborted) throw new Error("afbrudt");

    const ticket = await ticketFor(file, sha1, scope, fallbackMessage);
    if (aborted) throw new Error("afbrudt");

    // Phase 2: the transfer, the remaining 85% of the bar.
    await new Promise<void>((resolve, reject) => {
      upload = new tus.Upload(file, {
        endpoint: ticket.tus.endpoint,
        // Bunny's TUS wants the credentials as headers, minted per upload and
        // passed through unread (the server signed them).
        headers: ticket.tus.headers,
        // The fingerprint tus-js-client resumes on — the file's identity plus
        // this upload's video id, so two different videos never share a resume.
        fingerprint: async () =>
          `bunny-${ticket.upload.id}-${file.name}-${file.size}`,
        retryDelays: [0, 1000, 3000, 5000, 10000],
        onProgress: (sent, total) =>
          onProgress?.(0.15 + 0.85 * (total ? sent / total : 0)),
        onError: (err) => reject(err),
        onSuccess: () => resolve(),
      });
      // Continue a previous attempt at its last offset, or start fresh.
      upload.findPreviousUploads().then((previous) => {
        if (previous.length > 0) upload!.resumeFromPreviousUpload(previous[0]!);
        upload!.start();
      });
    });

    return ticket.upload;
  })();

  return {
    done,
    abort: () => {
      aborted = true;
      void upload?.abort(true);
    },
  };
}

async function ticketFor(
  file: File,
  sha1: string,
  scope: UploadScope,
  fallbackMessage: string,
): Promise<VideoUploadTicket> {
  try {
    return dataOf<VideoUploadTicket>(
      await thetaApi.http.POST("/uploads/video", {
        body: { filename: file.name, sha1, filesize: file.size, scope },
      }),
    );
  } catch (err) {
    if (isThetaError(err) && err.message) throw new Error(err.message);
    throw new Error(fallbackMessage);
  }
}
